import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";

import type { FCM } from './fcm';
import { PosDevice } from './device';
import { QrScan } from './qrscan';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { UserType } from '../types';
import { QrScanType } from '../types';
import { PosDeviceType } from '../types';
import { FCMType } from '../types';

const _schema = {
    name: "SESSION",
    "fields": [
        { name: "status", type: "string", default: "'AC'" },
        { name: "id", type: "string" },
        { name: "deviceid", type: "string" },
        { name: "posdevice", alias: "device", type: "objid" },
        { name: "user", type: "objid" },    
        { name: "qrscan", type: "objid" },
        { name: "touch", type: "date" },
        { name: "mode", type: "string", default: "POS" }
    ],
    relate: [
        { direction: ">", target: "USER", by: "user", name: "user", reason: "user", child: false, class: UserType },
        { direction: ">", target: "QRSCAN", by: "qrscan", name: "qrscan", reason: "qrscan", child: false, class: QrScanType },
        { direction: "<", target: "FCM", by: "session", name: "fcm", reason: "fcms", child: false, class: FCMType },
        { direction: ">", target: "POSDEVICE", by: "posdevice", name: "device", reason: "device", child: false, class: PosDeviceType },
    ]        
} as const;

const _SessionClass = CreateObject(_schema);
if (!_SessionClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class Session extends _SessionClass { 
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null, id: string | null = null){
        super(_schema, objid, data, objoptions || {});
        if (id){
            this.id = id;
        }
    }

    Copy(store: Array<DataObject> = []): Session {
        return this._Copy(store) as Session;
    }

    override get _uuid(): string {
        return this.id || super._uuid;
    }

    override DoUpdate(): void {
        this._onUpdated.next(this);
    }

    /**
     * A SESSION row arriving with `qrscan` set does not carry the parent
     * QRSCAN children list, so `qrscan.sessions` would stay empty until a
     * full reload. Register in / unregister from the scan when that link changes.
     *
     * Fan-in table chrome (`QrCodeView.IsConnected`) through the tenant mesa only.
     */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf){
            return;
        }

        // Keep PosDevice.sessions in sync for PTF presence (Pinpad.Connected / Ptf*).
        if (relation === 'posdevice' && related instanceof PosDevice){
            if (related === this.device){
                related.AddSession(this);
            }
            else {
                related.DelSession(this);
            }
        }

        if (!this.Commited){
            return;
        }

        if (relation === 'qrscan' && related instanceof QrScan){
            if (related === this.qrscan){
                related.AddSession(this);
            }
            else {
                related.DelSession(this);
            }

            this._NotifyConnected(related);
        }

        if (relation === 'user' && related){
            related.DoRefresh('SESSION', false);
        }
    }

    protected override OnUpdate(info: any): boolean {
        const changed = super.OnUpdate(info);
        if (changed && !this.CopyOf && this.Commited){
            this._NotifyConnected(this.qrscan);
            this.user?.DoRefresh('SESSION', false);
            this.device?.DoRefresh('SESSION', false);
        }

        return changed;
    }

    /** Invalidate table online mark on the tenant mesa (`QRCODE`). */
    private _NotifyConnected(scan: QrScan | null): void {
        scan?.qrcode?.DoRefresh('SESSION', false);
    }
    
    /****************************/
    /* CUSTOM METHODS           */
    /****************************/
    
    private _lastLivenessCheck = 0;

    /**
     * Guest and staff liveness for POS chrome.
     * - DE → not online.
     * - AC without `touch` in the last 10 minutes → zombie (treat as offline).
     * Server expire / unload publish DE via place changelog; thumbs do not poll.
     */
    get IsActive(): boolean {
        if (this.status !== 'AC' || !this.touch){
            return false;
        }

        return this.touch.getTime() > (Date.now() - 600 * 1000);
    }

    /**
     * Reloads this SESSION row from the server (central DB via objidrequest).
     * Punctual fetch only — table thumbs do not poll this path.
     */
    async RefreshFromServer(): Promise<boolean> {
        if (!this.objid){
            return false;
        }

        const _now = Date.now();
        if (this._lastLivenessCheck && ((_now - this._lastLivenessCheck) < 10000)){
            return false;
        }

        this._lastLivenessCheck = _now;
        const _data: any = await this.data.FetchByObjid(this.table, this.objid);
        if (_data?.['errorcode'] != 0 || !_data['info']){
            return false;
        }

        this._OnUpdate({ ..._data['info'], table: this.table });
        this._NotifyConnected(this.qrscan);
        
        return true;
    }

    /**
     * Latest FCM token row for this session (server keeps `order by objid desc limit 1`).
     */
    get fcm(): FCM | null {
        return this.fcms[0] || null;
    }

    set fcm(value: FCM | null) {
        for (const item of [...this.fcms]) {
            this.DelFcm(item);
        }
        
        if (value) {
            this.AddFcm(value);
            value.session = this;
        }
    }
}

